import React, { useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  MenuItem,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import RouteLayout from "../../../Components/Layout/RouteLayout";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate, useParams } from "react-router-dom";
import {
  useAddComponentMutation,
  useGetBookingFacilityQuery,
  useSetupFacilityMutation,
} from "../../../../api/financeApi";
import { toast } from "react-toastify";
import { statesInNigeria } from "../states";

const AddFacilities = () => {
  const paramsId = useParams();
  const [onAddNewFacility, { isLoading }] = useSetupFacilityMutation();
  const navigate = useNavigate();

  const payload = {
    bookingServiceId: paramsId?.id,
  };

  const { data: item } = useGetBookingFacilityQuery(payload);

  console.log("item", item?.result?.items[0]?.id);

  // Individual states for each facility field
  const [facilityName, setFacilityName] = useState("");
  const [facilityDescription, setFacilityDescription] = useState("");
  const [quantity, setQuantity] = useState(0);
  const [availableQuantity, setAvailableQuantity] = useState(0);
  const [status, setStatus] = useState("Available");
  const [locationId, setLocationId] = useState();
  const [locationName, setLocationName] = useState("");
  const [bookingPrice, setBookingPrice] = useState(0);
  const [image, setImage] = useState(null);
  const [fileName, setFileName] = useState("");

  const [component, setComponent] = useState({
    name: "",
    optional: false,
    description: "",
    quantity: 0,
    status: 0,
    location: "",
    optionalPrice: "",
  });

  const handleFacilityChange = (e) => {
    const { name, value } = e.target;
    switch (name) {
      case "facilityName":
        setFacilityName(value);
        break;
      case "facilityDescription":
        setFacilityDescription(value);
        break;
      case "quantity":
        setQuantity(parseInt(value, 10));
        break;
      case "availableQuantity":
        setAvailableQuantity(parseInt(value, 10));
        break;
      case "status":
        setStatus(value);
        break;
      case "locationName":
        setLocationName(value);
        break;
      case "bookingPrice":
        setBookingPrice(parseFloat(value));
        break;
      default:
        break;
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFileName(file);
      if (
        file.type === "image/png" ||
        file.type === "image/jpg" ||
        file.type === "image/jpeg"
      ) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setImage(reader.result);
        };
        reader.readAsDataURL(file);
      } else {
        toast.error(
          "You cannot upload this file type: Hint */jpg */jpeg */png"
        );
        return;
      }
    }
  };

  const handleFacility = async () => {
    try {
      const res = await onAddNewFacility({
        bookingServiceId: paramsId?.id,
        facilityName,
        facilityDescription,
        quantity,
        availableQuantity,
        status,
        locationId,
        locationName,
        bookingPrice,
        image: {
          file: image.replace(/^data:image\/[a-z]+;base64,/, ""),
          fileName: fileName?.name,
          documentType: 104,
          contentType: fileName?.type,
        },
      }).unwrap();

      if (res?.successful) {
        toast.success(res?.statusMessage);
        navigate(-1);
      } else {
        toast.error(res?.statusMessage);
      }
    } catch (err) {
      toast.error(err?.statusMessage);
    }
  };

  const handleComponentChange = (e) => {
    const { name, value } = e.target;
    setComponent((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const [onSubmitComponent, { isLoading: loadingComponent }] =
    useAddComponentMutation();

  const handleSubmit = async () => {
    try {
      const res = await onSubmitComponent({
        bookingServiceFacilityId: item?.result?.items[0]?.id,
        componentName: component.name,
        componentDescription: component.description,
        quantity: component.quantity,
        status: component.status,
        isOptional: component.optional,
        optionalPrice: component.optionalPrice,
      }).unwrap();
      if (res?.successful) {
        toast.success(res?.statusMessage);
        navigate(-1);
      } else {
        toast.error(res?.statusMessage);
      }
    } catch (err) {
      toast.error(err?.statusMessage);
    }
  };

  return (
    <RouteLayout title="Service Setup">
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(-1)}
        sx={{ color: "#305F32", mb: 2 }}
        variant="outlined"
      >
        Back
      </Button>
      <Box className="p-[20px]">
        <Typography variant="h5" sx={{ mb: 4 }}>
          Add Facilities
        </Typography>
        <Paper elevation={3} sx={{ padding: 4, borderRadius: 2, mb: 4 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
            Basic Information
          </Typography>
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              label="Facility Name"
              name="facilityName"
              value={facilityName}
              onChange={handleFacilityChange}
              required
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Description"
              name="facilityDescription"
              value={facilityDescription}
              onChange={handleFacilityChange}
              required
              multiline
              rows={3}
              sx={{ mb: 2 }}
            />
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: 2,
              }}
            >
              <TextField
                label="Quantity"
                name="quantity"
                type="number"
                value={quantity}
                onChange={handleFacilityChange}
                required
              />
              <TextField
                label="Available Quantity"
                name="availableQuantity"
                type="number"
                value={availableQuantity}
                onChange={handleFacilityChange}
                required
              />
              <TextField
                select
                label="Status"
                name="status"
                value={status}
                onChange={handleFacilityChange}
                required
              >
                <MenuItem value={1}>Active</MenuItem>
                <MenuItem value={0}>Inactive</MenuItem>
                <MenuItem value={2}>Maintenance</MenuItem>
              </TextField>
              <TextField
                select
                label="Location"
                name="locationName"
                value={locationName}
                onChange={handleFacilityChange}
                required
              >
                {statesInNigeria.map((item) => (
                  <MenuItem
                    onClick={() => setLocationId(item.id)}
                    key={item.id}
                    value={item.name}
                  >
                    {item.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Booking Price"
                name="bookingPrice"
                type="number"
                value={bookingPrice}
                onChange={handleFacilityChange}
                required
              />
            </Box>
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                Upload Supporting Image
              </Typography>
              <Button
                variant="outlined"
                component="label"
                startIcon={<UploadFileIcon />}
                sx={{ mb: 2, width: "100%" }}
              >
                {fileName ? fileName?.name : "Upload Image (JPG/PNG)"}
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={handleImageUpload}
                  required
                />
              </Button>
            </Box>
            {/* <Button variant="outlined" fullWidth sx={{ mt: 2 }}>
              + Add More Facilities
            </Button> */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 2,
                marginTop: 10,
              }}
            >
              <Button
                variant="contained"
                color="success"
                type="submit"
                disabled={isLoading}
                onClick={handleFacility}
              >
                {isLoading ? "Submitting..." : "Submit"}
              </Button>
            </Box>
          </Box>
        </Paper>

        <Typography variant="h5" sx={{ mb: 4 }}>
          Add Components
        </Typography>
        <Paper elevation={3} sx={{ padding: 4, borderRadius: 2, mb: 4 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
            Basic Information
          </Typography>
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              label="Name"
              name="name"
              value={component.name}
              onChange={handleComponentChange}
              required
              sx={{ mb: 2 }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={component.optional}
                  onChange={(e) =>
                    setComponent({ ...component, optional: e.target.checked })
                  }
                />
              }
              label="Optional"
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Description"
              name="description"
              value={component.description}
              onChange={handleComponentChange}
              required
              multiline
              rows={3}
              sx={{ mb: 2 }}
            />
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: 2,
              }}
            >
              <TextField
                label="Quantity"
                name="quantity"
                type="number"
                value={component.quantity}
                onChange={handleComponentChange}
                required
              />
              <TextField
                select
                label="Status"
                name="status"
                value={component.status}
                onChange={handleComponentChange}
                required
              >
                <MenuItem value={1}>Active</MenuItem>
                <MenuItem value={0}>Inactive</MenuItem>
                <MenuItem value={2}>Maintenance</MenuItem>
              </TextField>
              <TextField
                select
                label="Location"
                name="location"
                value={component.location}
                onChange={handleComponentChange}
                required
              >
                {statesInNigeria.map((item) => (
                  <MenuItem
                    onClick={() => setLocationId(item.id)}
                    key={item.id}
                    value={item.name}
                  >
                    {item.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Optional Price"
                name="optionalPrice"
                value={component.optionalPrice}
                onChange={handleComponentChange}
                required
              />
            </Box>
            {/* <Button variant="outlined" fullWidth sx={{ mt: 2 }}>
              + Add More Components
            </Button> */}
          </Box>
        </Paper>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <Button onClick={() => navigate(-1)} variant="outlined">
            Cancel
          </Button>
          <Button
            disabled={loadingComponent}
            variant="contained"
            color="success"
            onClick={handleSubmit}
          >
            {loadingComponent ? "Submitting..." : "Submit"}
          </Button>
        </Box>
      </Box>
    </RouteLayout>
  );
};

export default AddFacilities;
